<!--
 * Path: src/core/components/dialogs/NewEntityDialog.vue
 * Task: Global modal that renders the BrowserContent form safely utilizing the isDraft context.
 * Refactoring: Migrated from editorStore to workspaceStore and useEntityOperations.
-->
<template>
  <BaseDialog
    v-model="workspaceStore.isNewEntityDialogOpen"
    max-width="800"
    @close="cancelCreation"
  >
    <!-- We render the Entity Header at the very top so it acts as the modal title -->
    <div v-if="workspaceStore.draftEntity" class="border-b bg-surface pb-2 pt-2 px-2 draft-header-wrapper" style="margin: -24px -24px 16px -24px;">
      <EntityHeaderMain :entity="workspaceStore.draftEntity" :is-secondary="false" />
    </div>

    <div style="max-height: 60vh; overflow-y: auto;" class="pe-2">
      <!-- We reuse the BrowserContent natively, isolating it via the FormContext injection -->
      <BrowserContent v-if="workspaceStore.draftEntity" />
    </div>

    <!-- Actions Footer -->
    <template #actions>
      <v-btn variant="text" @click="cancelCreation">Cancel</v-btn>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        variant="flat"
        :loading="uiStore.loading.save"
        @click="saveDraft"
      >
        Create Entity
      </v-btn>
    </template>
  </BaseDialog>
</template>

<script setup lang="ts">
import { computed, provide, ref } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { FormContextKey } from '@/core/keys';
import BrowserContent from '@/features/browser/components/BrowserContent.vue';
import EntityHeaderMain from '@/core/components/entityHeader/EntityHeaderMain.vue';
import BaseDialog from './BaseDialog.vue';

const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const { saveEntity } = useEntityOperations();

// Since the global edit mode was removed earlier, we force this to always be unlocked (editable) for creation
const draftIsLocked = ref(false);

// Dependency Injection to tell BrowserContent to read/write to `draftEntity` instead of `selectedEntity`
provide(FormContextKey, {
  isSecondary: false,
  isDraft: true, // <--- The magic flag!
  entity: computed(() => workspaceStore.draftEntity),
  entityId: computed(() => workspaceStore.draftEntity?.id || null),
  isLocked: draftIsLocked
});

const cancelCreation = () => {
  workspaceStore.resetSelectedEntity(false, true); // true = isDraft
};

const saveDraft = async () => {
  // Call the composable action explicitly stating this is a draft save
  await saveEntity(false, true);
};
</script>

<style scoped>
/* Blendet die Status- und Versions-Badges (Chips) aus dem Header aus, aber NUR in diesem Dialog */
.draft-header-wrapper :deep(.v-chip) {
  display: none !important;
}
</style>
